import { DOCTOR_COPY } from './doctorCopy';
import { HEAD_DOCTOR_COPY } from '../head-doctor/headDoctorCopy';
import { ROLES } from '../../constants/roles';

export const DOCTOR_BASE = '/doctor';
export const HEAD_DOCTOR_BASE = '/head-doctor';
export const DENTIST_BASE = '/dentist';

export function buildDoctorPaths(basePath = DOCTOR_BASE) {
  return {
    DASHBOARD: basePath,
    DOSSIER: `${basePath}/dossier`,
    CONSULTATIONS: `${basePath}/consultations`,
  /** Saisie consultation plein écran (nouvelle fiche — pas de popup). */
    CONSULTATION_NEW: `${basePath}/consultations/new`,
    DIAGNOSTIC: `${basePath}/diagnostic`,
    PRESCRIPTIONS: `${basePath}/prescriptions`,
    PHARMACY_LOCAL: `${basePath}/pharmacie`,
    EXAMENS: `${basePath}/examens`,
    ANALYSES: `${basePath}/analyses`,
    RADIOGRAPHIES: `${basePath}/radiographies`,
    ECHOGRAPHIES: `${basePath}/echographies`,
    PARTENAIRES: `${basePath}/partenaires`,
    ORIENTATIONS: `${basePath}/orientations`,
    CERTIFICATS: `${basePath}/certificats`,
    MALADIES: `${basePath}/maladies`,
    JOURS_REPOS: `${basePath}/jours-repos`,
    SOINS_INFIRMERIE: `${basePath}/soins-infirmerie`,
    TESTS_INFIRMERIE: `${basePath}/tests-infirmerie`,
    VACCINS: `${basePath}/vaccins`,
    PLANIFICATION_FAMILIALE: `${basePath}/planning-familial`,
    LETTRES_REFERENCE: `${basePath}/lettres-reference`,
    RENDEZ_VOUS: `${basePath}/rendez-vous`,
    STATISTIQUES: `${basePath}/statistiques`,
    STATISTIQUES_SYNTHESE: `${basePath}/statistiques/synthese`,
    STATISTIQUES_PATHOLOGIES: `${basePath}/statistiques/pathologies`,
    STATISTIQUES_MORBIDITE: `${basePath}/statistiques/morbidite`,
    STATISTIQUES_ORDONNANCES: `${basePath}/statistiques/ordonnances`,
    STATISTIQUES_MEDICAMENTS: `${basePath}/statistiques/medicaments`,
    STATISTIQUES_CONSULTATIONS: `${basePath}/statistiques/consultations`,
    STATISTIQUES_SOINS: `${basePath}/statistiques/soins-infirmerie`,
    STATISTIQUES_EXAMENS: `${basePath}/statistiques/examens`,
    STATISTIQUES_WORK_ACCIDENTS: `${basePath}/statistiques/accidents-travail`,
    STATISTIQUES_OCCUPATIONAL_DISEASES: `${basePath}/statistiques/maladies-professionnelles`,
    STATISTIQUES_JOURS_REPOS: `${basePath}/statistiques/jours-repos`
  };
}

export const DOCTOR_PATHS = buildDoctorPaths(DOCTOR_BASE);
export const HEAD_DOCTOR_PATHS = {
  ...buildDoctorPaths(HEAD_DOCTOR_BASE),
  PRESCRIPTIONS: `${HEAD_DOCTOR_BASE}/statistiques/ordonnances`
};
export const DENTIST_PATHS = buildDoctorPaths(DENTIST_BASE);

const NAV = DOCTOR_COPY.NAV;

/** Mapping des clés métier → URL (hub + navigation). */
export function getDoctorRoutePaths(paths = DOCTOR_PATHS) {
  return {
    DOSSIER: paths.DOSSIER,
    CONSULTATIONS: paths.CONSULTATIONS,
    DIAGNOSTIC: paths.DIAGNOSTIC,
    PRESCRIPTIONS: paths.PRESCRIPTIONS,
    EXAMENS: paths.EXAMENS,
    ANALYSES: paths.ANALYSES,
    RADIOGRAPHIES: paths.RADIOGRAPHIES,
    ECHOGRAPHIES: paths.ECHOGRAPHIES,
    PARTENAIRES: paths.PARTENAIRES,
    ORIENTATIONS: paths.ORIENTATIONS,
    CERTIFICATS: paths.CERTIFICATS,
    MALADIES: paths.MALADIES,
    JOURS_REPOS: paths.JOURS_REPOS,
    SOINS_INFIRMERIE: paths.SOINS_INFIRMERIE,
    TESTS_INFIRMERIE: paths.TESTS_INFIRMERIE,
    VACCINS: paths.VACCINS,
    PLANIFICATION_FAMILIALE: paths.PLANIFICATION_FAMILIALE,
    LETTRES_REFERENCE: paths.LETTRES_REFERENCE,
    RENDEZ_VOUS: paths.RENDEZ_VOUS,
    STATISTIQUES: paths.STATISTIQUES_SYNTHESE,
    STATISTIQUES_SYNTHESE: paths.STATISTIQUES_SYNTHESE,
    STATISTIQUES_PATHOLOGIES: paths.STATISTIQUES_PATHOLOGIES,
    STATISTIQUES_MORBIDITE: paths.STATISTIQUES_MORBIDITE,
    STATISTIQUES_ORDONNANCES: paths.STATISTIQUES_ORDONNANCES,
    STATISTIQUES_MEDICAMENTS: paths.STATISTIQUES_MEDICAMENTS,
    STATISTIQUES_CONSULTATIONS: paths.STATISTIQUES_CONSULTATIONS,
    STATISTIQUES_SOINS: paths.STATISTIQUES_SOINS,
    STATISTIQUES_EXAMENS: paths.STATISTIQUES_EXAMENS,
    STATISTIQUES_WORK_ACCIDENTS: paths.STATISTIQUES_WORK_ACCIDENTS,
    STATISTIQUES_OCCUPATIONAL_DISEASES: paths.STATISTIQUES_OCCUPATIONAL_DISEASES,
    STATISTIQUES_JOURS_REPOS: paths.STATISTIQUES_JOURS_REPOS
  };
}

/** @param {boolean} canPrescribe */
export function getDoctorNavItems({ canPrescribe, isHeadDoctor = false, paths = DOCTOR_PATHS, showLocalPharmacy = false }) {
  const model = getDoctorMenuModel({ canPrescribe, isHeadDoctor, paths, showLocalPharmacy });
  const grouped = model.groups?.flatMap((group) => group.items) || [];
  const nested = model.submenus?.flatMap((submenu) => submenu.items) || [];
  return [...model.quickItems, ...grouped, ...nested];
}

/** @param {{ canPrescribe: boolean, isHeadDoctor?: boolean, paths?: object, showLocalPharmacy?: boolean }} params */
export function getDoctorMenuModel({ canPrescribe, isHeadDoctor = false, paths = DOCTOR_PATHS, showLocalPharmacy = false }) {
  const layout = isHeadDoctor ? HEAD_DOCTOR_COPY.LAYOUT : DOCTOR_COPY.LAYOUT;
  const nav = isHeadDoctor ? HEAD_DOCTOR_COPY.NAV : NAV;

  if (isHeadDoctor) {
    const quickItems = [
      { to: paths.DASHBOARD, end: true, ...nav.dashboard },
      { to: paths.DOSSIER, end: false, ...nav.dossier },
      { to: paths.CONSULTATIONS, end: false, ...nav.consultations },
      { to: paths.RENDEZ_VOUS, end: false, ...nav.rendezVous }
    ];

    const groups = [
      {
        key: 'statistiques',
        label: layout.SIDE_GROUP_STATS,
        hint: layout.SIDE_GROUP_STATS_HINT,
        icon: 'chartBar',
        items: [
          { to: paths.STATISTIQUES_SYNTHESE, end: true, ...nav.statistiquesSynthese },
          { to: paths.STATISTIQUES_CONSULTATIONS, end: false, ...nav.statistiquesConsultations },
          { to: paths.STATISTIQUES_JOURS_REPOS, end: false, ...nav.statistiquesRepos },
          { to: paths.STATISTIQUES_MORBIDITE, end: false, ...nav.statistiquesMorbidite },
          { to: paths.STATISTIQUES_PATHOLOGIES, end: false, ...nav.statistiquesPathologies },
          { to: paths.STATISTIQUES_ORDONNANCES, end: false, ...nav.statistiquesOrdonnances },
          { to: paths.STATISTIQUES_MEDICAMENTS, end: false, ...nav.statistiquesMedicaments },
          { to: paths.STATISTIQUES_SOINS, end: false, ...nav.statistiquesSoins },
          { to: paths.STATISTIQUES_EXAMENS, end: false, ...nav.statistiquesExamens }
        ]
      },
      {
        key: 'examens',
        label: layout.SIDE_GROUP_EXAMENS,
        hint: layout.SIDE_GROUP_EXAMENS_HINT,
        icon: 'chartBar',
        items: [
          { to: paths.ANALYSES, end: false, ...nav.analyses },
          { to: paths.RADIOGRAPHIES, end: false, ...nav.radiographies },
          { to: paths.ECHOGRAPHIES, end: false, ...nav.echographies }
        ]
      },
      {
        key: 'referentiels',
        label: layout.SIDE_GROUP_REFERENTIAL,
        hint: layout.SIDE_GROUP_REFERENTIAL_HINT,
        icon: 'alertCircle',
        items: [
          { to: paths.MALADIES, end: false, ...nav.maladies },
          { to: paths.VACCINS, end: false, ...nav.vaccins },
          { to: paths.JOURS_REPOS, end: false, ...nav.joursRepos }
        ]
      },
      {
        key: 'infirmerie',
        label: layout.SIDE_GROUP_INFIRMERIE,
        hint: layout.SIDE_GROUP_INFIRMERIE_HINT,
        icon: 'heart',
        items: [
          { to: paths.SOINS_INFIRMERIE, end: false, ...nav.soinsInfirmierie },
          { to: paths.TESTS_INFIRMERIE, end: false, ...nav.testsInfirmierie },
          { to: paths.PLANIFICATION_FAMILIALE, end: false, ...nav.planificationFamiliale }
        ]
      },
      {
        key: 'reseau',
        label: layout.SIDE_GROUP_NETWORK,
        hint: layout.SIDE_GROUP_NETWORK_HINT,
        icon: 'building',
        items: [
          { to: paths.PARTENAIRES, end: false, ...nav.partenaires },
          { to: paths.ORIENTATIONS, end: false, ...nav.orientations }
        ]
      }
    ];

    return { quickItems, groups, submenus: [] };
  }

  const quickItems = [
    { to: paths.DASHBOARD, end: true, icon: 'home', ...nav.dashboard },
    { to: paths.DOSSIER, end: false, icon: 'user', ...nav.dossier },
    { to: paths.CONSULTATIONS, end: false, icon: 'stethoscope', ...nav.consultations }
  ];
  if (canPrescribe) {
    quickItems.push({ to: paths.PRESCRIPTIONS, end: false, icon: 'pills', ...nav.prescriptions });
  }
  if (showLocalPharmacy) {
    quickItems.push({
      to: paths.PHARMACY_LOCAL,
      end: false,
      icon: 'building',
      ...nav.pharmacieLocale
    });
  }
  return { quickItems, submenus: [] };
}

export function physicianCanPrescribe(role) {
  return role === ROLES.DOCTOR || role === ROLES.HEAD_DOCTOR;
}

export function isHeadDoctorRole(role) {
  return role === ROLES.HEAD_DOCTOR;
}

/** Éditer une consultation en page (médecin · dentiste). */
export function doctorConsultationEditPath(consultationId, paths = DOCTOR_PATHS) {
  return `${paths.CONSULTATIONS}/${encodeURIComponent(String(consultationId))}/edit`;
}
